import type { Guest, GuestRelationshipLink } from '../../types';

/**
 * Rozpozná typ vazby „rodina“ podle názvu z číselníku (výchozí CS/EN i vlastní názvy).
 * Slouží k řazení: rodinné skupiny první, pak ostatní propojené, jednotlivci nakonec.
 */
export function isFamilyRelationshipLabel(label: string): boolean {
  const t = label.trim().toLowerCase();
  if (t.includes('rodin')) return true;
  if (t === 'family' || /^family(\s|[\/\-]|$)/.test(t)) return true;
  return false;
}

/** Odstraní ruční prefix typu „(0) “ z názvu jen pro zobrazení v seznamu. */
export function displayGuestListName(fullName: string): string {
  const t = fullName.replace(/^\s*\(\d+\)\s+/, '').trim();
  return t || fullName;
}

class UnionFind {
  private parent = new Map<string, string>();

  constructor(ids: Iterable<string>) {
    for (const id of ids) this.parent.set(id, id);
  }

  find(x: string): string {
    let p = this.parent.get(x);
    if (p === undefined) return x;
    const root = p === x ? x : this.find(p);
    this.parent.set(x, root);
    return root;
  }

  union(a: string, b: string): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra === rb) return;
    // stabilní reprezentant: lexikograficky menší UUID
    if (ra < rb) this.parent.set(rb, ra);
    else this.parent.set(ra, rb);
  }
}

/**
 * Seřadí hosty: 1) clustery s aspoň jednou rodinnou vazbou, 2) ostatní propojené skupiny,
 * 3) jednotlivci bez vazby v rámci seznamu. Uvnitř skupiny stejného tieru platí cluster + jméno.
 */
export function sortGuestsByRelationshipClusters(
  guests: Guest[],
  links: GuestRelationshipLink[],
  familyTypeIds: Set<string> = new Set(),
): Guest[] {
  if (guests.length <= 1) return [...guests];
  const ids = new Set(guests.map((g) => g.id));
  const uf = new UnionFind(ids);
  for (const l of links) {
    if (ids.has(l.guest_low_id) && ids.has(l.guest_high_id)) {
      uf.union(l.guest_low_id, l.guest_high_id);
    }
  }

  const tierByRoot = new Map<string, 0 | 1 | 2>();
  const uniqueRoots = new Set<string>();
  for (const id of ids) {
    uniqueRoots.add(uf.find(id));
  }
  for (const root of uniqueRoots) {
    const members = [...ids].filter((id) => uf.find(id) === root);
    const memberSet = new Set(members);
    const internalLinks = links.filter(
      (l) => memberSet.has(l.guest_low_id) && memberSet.has(l.guest_high_id),
    );
    let tier: 0 | 1 | 2;
    if (members.length === 1 && internalLinks.length === 0) {
      tier = 2;
    } else if (internalLinks.some((l) => familyTypeIds.has(l.relationship_type_id))) {
      tier = 0;
    } else {
      tier = 1;
    }
    tierByRoot.set(root, tier);
  }

  return [...guests].sort((a, b) => {
    const ra = uf.find(a.id);
    const rb = uf.find(b.id);
    const ta = tierByRoot.get(ra) ?? 2;
    const tb = tierByRoot.get(rb) ?? 2;
    if (ta !== tb) return ta - tb;
    if (ra !== rb) return ra.localeCompare(rb);
    return a.full_name.localeCompare(b.full_name, undefined, { sensitivity: 'base' });
  });
}

/** Kořen clusteru pro hosta (v rámci daného seznamu a vazeb). */
export function clusterRootForGuest(guests: Guest[], links: GuestRelationshipLink[], guestId: string): string {
  const ids = new Set(guests.map((g) => g.id));
  const uf = new UnionFind(ids);
  for (const l of links) {
    if (ids.has(l.guest_low_id) && ids.has(l.guest_high_id)) {
      uf.union(l.guest_low_id, l.guest_high_id);
    }
  }
  return uf.find(guestId);
}

/**
 * Rozdělí již seřazené hosty na souvislé úseky se stejným cluster rootem (pro vizuální oddělení skupin).
 */
export function chunkGuestsByClusterRoot(guests: Guest[], links: GuestRelationshipLink[]): Guest[][] {
  if (guests.length === 0) return [];
  const ids = new Set(guests.map((g) => g.id));
  const uf = new UnionFind(ids);
  for (const l of links) {
    if (ids.has(l.guest_low_id) && ids.has(l.guest_high_id)) {
      uf.union(l.guest_low_id, l.guest_high_id);
    }
  }
  const chunks: Guest[][] = [];
  let cur: Guest[] = [];
  let prevRoot: string | null = null;
  for (const g of guests) {
    const r = uf.find(g.id);
    if (prevRoot !== null && r !== prevRoot) {
      chunks.push(cur);
      cur = [];
    }
    cur.push(g);
    prevRoot = r;
  }
  if (cur.length) chunks.push(cur);
  return chunks;
}

const ACCENT_BORDER = [
  'border-l-2 border-slate-200 dark:border-slate-700',
  'border-l-2 border-sky-200/90 dark:border-sky-800/80',
  'border-l-2 border-violet-200/90 dark:border-violet-800/70',
  'border-l-2 border-teal-200/90 dark:border-teal-800/70',
  'border-l-2 border-amber-200/90 dark:border-amber-800/70',
  'border-l-2 border-rose-200/80 dark:border-rose-900/50',
] as const;

export function clusterAccentClass(rootId: string): string {
  let h = 0;
  for (let i = 0; i < rootId.length; i++) h = (h * 31 + rootId.charCodeAt(i)) | 0;
  const idx = Math.abs(h) % ACCENT_BORDER.length;
  return ACCENT_BORDER[idx]!;
}

export interface GuestLinkGroupForList {
  typeId: string;
  typeLabel: string;
  otherNames: string[];
}

/** Seskupí vazby hosta podle typu; jména bez ručního prefixu (0) pro zobrazení. */
export function groupGuestLinksForList(
  guestId: string,
  links: GuestRelationshipLink[],
  guestById: Map<string, Guest>,
  typeLabelById: Map<string, string>,
): GuestLinkGroupForList[] {
  const mine = links.filter((l) => l.guest_low_id === guestId || l.guest_high_id === guestId);
  const byType = new Map<string, string[]>();
  for (const link of mine) {
    const otherId = link.guest_low_id === guestId ? link.guest_high_id : link.guest_low_id;
    const raw = guestById.get(otherId)?.full_name ?? otherId;
    const name = displayGuestListName(raw);
    const tid = link.relationship_type_id;
    let arr = byType.get(tid);
    if (!arr) {
      arr = [];
      byType.set(tid, arr);
    }
    if (!arr.includes(name)) arr.push(name);
  }
  for (const arr of byType.values()) {
    arr.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }
  return [...byType.entries()]
    .map(([typeId, otherNames]) => ({
      typeId,
      typeLabel: typeLabelById.get(typeId) ?? '',
      otherNames,
    }))
    .sort((a, b) => a.typeLabel.localeCompare(b.typeLabel, undefined, { sensitivity: 'base' }));
}
